<template>
	<main>
		<section class="reason_wrap">
			<div class="reason_container c_inner">
				<div class="reason_title">
					<h3 class="title">{{ CANCEL_SUBSCRIPTION_REASON }}</h3>
					<p class="guide">{{ CANCEL_SUBSCRIPTION_REASON_GUIDE }}</p>
				</div>
				<div v-if="unsubscribeListData" class="reason_content">
					<div v-for="item in unsubscribeListData.reasons" :key="item.unsubscribe_pk" class="content_container">
						<label v-if="item.unsubscribe_sentence !== '미선택'" class="content_label">
							<input v-model="selectedPks" class="label_input" type="checkbox" :value="item.unsubscribe_pk" />
							{{ item.unsubscribe_sentence }}
						</label>
						<div
							v-if="
								(item.unsubscribe_pk === 7 && selectedPks.includes(7)) ||
								(item.unsubscribe_pk === 8 && selectedPks.includes(8))
							"
							class="textArea_wrap"
						>
							<textarea
								v-model="textAreas[item.unsubscribe_pk]"
								class="c_textarea content_textarea"
								maxlength="50"
								:placeholder="PLACEHOLDER_TITLE_50"
							></textarea>
							<span class="count">{{ textAreas[item.unsubscribe_pk].length }}/50</span>
						</div>
					</div>
				</div>
				<button class="c_button primary-fill reason_button" type="button" @click="submitReason">제출하기</button>
			</div>
		</section>
	</main>
</template>

<script setup lang="ts">
// core
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// api
import { useGetUnsubscribeListApi, useMemberUnsubscribeApi, useSaveUnsubscribeReasonsApi } from '@/api/mypage';
// constant
import { CANCEL_SUBSCRIPTION_REASON, CANCEL_SUBSCRIPTION_REASON_GUIDE, PLACEHOLDER_TITLE_50 } from '@/constants/title';
// store
import { usePaymentStore } from '@/stores/payment';

// type
interface TextAreasType {
	[key: number]: string;
}
// -----variable-----
const route = useRoute();
const router = useRouter();
const paymentStore = usePaymentStore();

const selectedPks = ref<number[]>([]);
const textAreas = ref<TextAreasType>({
	7: '',
	8: '',
});
// -----api-----
const { data: unsubscribeListData } = useGetUnsubscribeListApi();
const { mutateAsync: saveUnsubscribeReasons } = useSaveUnsubscribeReasonsApi();
const { mutateAsync: memberUnsubscribe } = useMemberUnsubscribeApi();
// -----function-----
const submitReason = async () => {
	// COMMENT :: 해지 사유 로딩 안된 상태에서 제출할 경우 unsubscribe_pks가 undefined로 들어갈 수 있는 상황 방지
	if (!unsubscribeListData.value) {
		return;
	}
	if (textAreas.value[7] && !selectedPks.value.includes(7)) {
		textAreas.value[7] = '';
	}
	if (textAreas.value[8] && !selectedPks.value.includes(8)) {
		textAreas.value[8] = '';
	}
	let unselectedPk;
	unsubscribeListData.value.reasons.forEach((item) => {
		if (item.unsubscribe_sentence === '미선택') {
			unselectedPk = item.unsubscribe_pk;
		}
	});
	const responseSaveReason = await saveUnsubscribeReasons({
		unsubscribe_pks: selectedPks.value.length !== 0 ? String(selectedPks.value) : String(unselectedPk),
		unsubscribe_app_contents: textAreas.value[7],
		unsubscribe_etc_contents: textAreas.value[8],
	});
	if (responseSaveReason.data.success) {
		if (paymentStore.getPayment === '앱 결제') {
			router.replace(`/sub/cancel-app-guide/${route.params.subscription_pk}`);
		} else {
			const responseUnsubscribe = await memberUnsubscribe({ subscription_pk: Number(route.params.subscription_pk) });
			if (responseUnsubscribe.data.success) {
				router.replace(`/sub/cancel-complete/${route.params.subscription_pk}`);
			} else {
				alert('유효하지 않은 구독 상품입니다.');
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.reason_wrap {
	max-width: 68rem;
	margin: var(--header-height) auto 0;
	padding: 11.2rem 0;
	@media screen and (max-width: 1024px) {
		margin: var(--mobile-header-height) auto 0;
		padding: 4rem 0;
	}
}
.reason_container {
	display: flex;
	flex-direction: column;
	gap: 2rem;
}
.reason_title {
	.title {
		font-size: var(--font-s-x-large);
		font-weight: var(--font-w-bold);
		line-height: normal;
	}
	.guide {
		font-size: var(--font-s-large);
		font-weight: var(--font-w-bold);
		line-height: 2.7rem;
	}
}
.reason_content {
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	.content_container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		.content_label {
			display: flex;
			align-items: center;
			gap: 0.8rem;
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-light);
			line-height: 1.2;
			input[type='checkbox'] {
				-webkit-appearance: none;
				-moz-appearance: none;
				width: 1.8rem;
				height: 1.8rem;
				margin-bottom: 0.1rem;
				background-color: transparent;
				border: 1px solid var(--color-base-light);
				border-radius: 3px;
				position: relative;
				cursor: pointer;
			}
			input[type='checkbox']:hover {
				border: none;
				box-shadow:
					0 0 0 1px var(--color-base-light),
					0 0 0 2px #ffa800;
			}
			input[type='checkbox']:checked {
				background-color: var(--color-primary);
				border: none;
				color: white;
			}
			input[type='checkbox']:checked::after {
				content: '';
				position: absolute;
				top: 0px;
				left: 5px;
				width: 5px;
				height: 10px;
				border: solid white; /* Change this to the desired checkmark color */
				border-width: 0 2px 2px 0;
				transform: rotate(45deg);
			}
		}
		.textArea_wrap {
			position: relative;
			border: 1px solid #818393;
			border-radius: 6px;
			height: 12rem;
			.content_textarea {
				border: none;
				height: 9rem;
				&:focus {
					border: none;
					outline: none;
					-webkit-box-shadow: none;
					-moz-box-shadow: none;
					box-shadow: none;
				}
			}
			.count {
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
				color: var(--color-base-mid);
				position: absolute;
				bottom: 1.1rem;
				right: 1rem;
			}
		}
	}
}
.reason_button {
	display: flex;
	height: 5.2rem;
	min-width: 7.7rem;
	min-height: 4rem;
	padding: 1.3rem 1.6rem;
	justify-content: center;
	align-items: center;
	gap: 1rem;
	align-self: stretch;
}
</style>
